import { useQuery } from "@apollo/client";
import { gql } from "@src/__generated__";
import React from "react";
import { Loader } from "@googlemaps/js-api-loader";
import { useAuth } from "@src/contexts/AuthProvider";

type GoogleMapsContext = {
  AutocompleteService: typeof google.maps.places.Autocomplete | undefined;
};
const GET_GOOGLE_MAPS_API_KEY = gql(/* GraphQL */ `
  query GetGoogleMapsApiKey {
    applicationConfiguration {
      googleMapsApiKey
    }
  }
`);

export const googleMapsContext = React.createContext<GoogleMapsContext>({
  AutocompleteService: undefined,
});

export const GoogleMapsProvider: React.FC = ({ children }) => {
  const [AutocompleteService, setAutocompleteService] =
    React.useState<typeof google.maps.places.Autocomplete>();
  const { tokenUserData, vendorUser } = useAuth();
  const { data } = useQuery(GET_GOOGLE_MAPS_API_KEY, {
    skip: !tokenUserData && !vendorUser,
  });

  React.useEffect(() => {
    if (
      !AutocompleteService &&
      data &&
      data.applicationConfiguration.googleMapsApiKey
    ) {
      const loader = new Loader({
        apiKey: data.applicationConfiguration.googleMapsApiKey,
        libraries: ["places"],
      });

      void loader.importLibrary("places").then(({ Autocomplete }) => {
        if (Autocomplete) {
          // Must leave this as a callback function, otherwise the
          // AutocompleteService will be instantiated since setState will invoke the constructor.
          // We want the definition of the class to be passed to the context, not an instance of it.
          setAutocompleteService(() => Autocomplete);
        }
      });
    }
  }, [data, AutocompleteService]);

  return (
    <googleMapsContext.Provider
      value={{ AutocompleteService: AutocompleteService }}
    >
      {children}
    </googleMapsContext.Provider>
  );
};
